import React from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import slugify from '~/techstyle-shared/react-components/src/utils/slugify';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import { filterCategoryTree } from '../../utils/categoryTree';

import DesktopSideNavSubcategoryTree from './DesktopSideNavSubcategoryTree';

const DynamicFormattedMessage = FormattedMessage;

const CategoryTreeList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.gridCategoryTreeTextColor};
  list-style: none;
`;

const CategoryTreeListItemLink = styled.a`
  text-decoration: ${({ $isActive }) => ($isActive ? 'underline' : 'none')};
  font-weight: ${({ $isActive }) => ($isActive ? '600' : 'normal')};

  &:hover {
    text-decoration: underline;
  }

  ${({ theme }) => theme.gridStyles.desktopCategoryItemStyle};
  ${({ $isActive, theme }) =>
    $isActive && theme.gridStyles.activeDesktopCategoryItemStyle}
`;

const CategoryTreeListItem = styled.li`
  padding-left: ${({ $shouldIndent }) => ($shouldIndent ? '16px' : '0px')};
`;

const DesktopSideNavCategoryTree = ({ activeCategory }) => {
  const { ancestor, categoryTree, isUsingSubcategoriesAsRoot } =
    filterCategoryTree(activeCategory);

  if (!activeCategory) {
    return null;
  }

  const formatAutoTag = (category) => {
    return category.label && typeof category.label === 'string'
      ? `prodgrid-filter-${
          category.isTopLevel ? 'all' : slugify(category.label, '-')
        }`
      : undefined;
  };

  return (
    <CategoryTreeList>
      {ancestor && (
        <li key={ancestor?.urlPath}>
          <CategoryTreeListItemLink
            href={ancestor?.urlPath}
            $isActive={ancestor?.urlPath === activeCategory?.urlPath}
            data-autotag={formatAutoTag(ancestor)}
          >
            <DynamicFormattedMessage
              id={`site_product_category.${slugify(ancestor.label)}`}
              defaultMessage={ancestor.label}
            />
          </CategoryTreeListItemLink>
        </li>
      )}
      {categoryTree?.map((category) => {
        return (
          <CategoryTreeListItem
            key={category?.urlPath}
            $shouldIndent={isUsingSubcategoriesAsRoot}
          >
            <CategoryTreeListItemLink
              href={category?.urlPath}
              $isActive={category?.urlPath === activeCategory?.urlPath}
              data-autotag={formatAutoTag(category)}
            >
              <DynamicFormattedMessage
                id={`site_product_category.${slugify(category.label)}`}
                defaultMessage={category.label}
              />
            </CategoryTreeListItemLink>
            <DesktopSideNavSubcategoryTree
              subcategories={category?.subcategories}
            />
          </CategoryTreeListItem>
        );
      })}
    </CategoryTreeList>
  );
};

DesktopSideNavCategoryTree.propTypes = {
  activeCategory: PropTypes.shape({
    ancestors: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        urlPath: PropTypes.string,
      })
    ),
    isTopLevel: PropTypes.bool,
    siblings: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        urlPath: PropTypes.string,
        subcategories: PropTypes.arrayOf(
          PropTypes.shape({
            label: PropTypes.string,
            urlPath: PropTypes.string,
          })
        ),
      })
    ),
    subcategories: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        urlPath: PropTypes.string,
        subcategories: PropTypes.arrayOf(
          PropTypes.shape({
            label: PropTypes.string,
            urlPath: PropTypes.string,
          })
        ),
      })
    ),
    urlPath: PropTypes.string,
  }),
};

export default DesktopSideNavCategoryTree;
